// components/EventRegistrationForm/TermsModal.js
import React from 'react';
import {
    X,
    FileText,
    AlertCircle,
    Check,
    ArrowLeft,
    CreditCard,
    Loader2
} from 'lucide-react';

const TERMS_AND_CONDITIONS = [
    "If the payment status shows Unpaid, Aborted, Awaited, Failed, or Invalid Request, or if the amount is debited but the status is not updated, do not attempt another payment immediately.",
    "Please wait for at least half a day (12 hours) after making the payment for the status to be updated automatically.",
    "For payment-related clarification, contact 0422-4344777 / 0422-4344444 (9:00 AM – 12:45 PM & 2:00 PM – 5:00 PM) or email cnce@psgtech.edu.",
    "Centre for Non-Formal & Continuing Education (CNCE) is not responsible for payments that do not reach the correct account due to incorrect information entered by the applicant.",
    "If the card issuer, bank, or UPI service provider declines the payment, Centre for Non-Formal & Continuing Education (CNCE) shall not be held liable.",
    "Centre for Non-Formal & Continuing Education (CNCE) will not be liable for any direct or indirect loss or damage arising from payment failure, technical issues, or third-party service interruptions.",
    "All payment details entered through this payment gateway are securely encrypted to ensure safe and secure transactions.",
    "Online payments can be made using UPI, Credit Card, Debit Card, or Net Banking through authorized payment service providers.",
    "This privacy policy applies to all products and services offered by the Centre for Non-Formal & Continuing Education (CNCE), and user data is handled as per applicable data protection regulations.",
    "Website content is provided to the best of our knowledge and may be updated or modified without prior notice.",
    "Centre for Non-Formal & Continuing Education (CNCE) reserves the right to modify or remove these terms, conditions, and privacy policies at any time.",
    "By proceeding with payment, the candidate confirms that all information provided in the application form is true and correct to the best of their knowledge."
];

const TermsModal = ({
    showTermsModal,
    setShowTermsModal,
    acceptedTerms,
    setAcceptedTerms,
    proceedToPayment,
    termsLoading
}) => {
    if (!showTermsModal) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <FileText className="w-6 h-6 mr-3" />
                            <h2 className="text-xl font-bold">Terms & Conditions</h2>
                        </div>
                        <button
                            onClick={() => setShowTermsModal(false)}
                            className="hover:text-gray-200"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <p className="text-sm text-blue-100 mt-2">
                        Please read carefully before proceeding to payment
                    </p>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto max-h-[55vh]">
                    <div className="space-y-4">
                        {TERMS_AND_CONDITIONS.map((term, index) => (
                            <div key={index} className="flex items-start">
                                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs mr-3 mt-1">
                                    {index + 1}
                                </div>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                    {term}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Important Note */}
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start">
                            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                            <p className="text-sm text-yellow-800">
                                Do not refresh or retry payment immediately if the amount is debited.
                                Always wait for the status update.
                            </p>
                        </div>
                    </div>

                    {/* Checkbox */}
                    <div className="mt-6">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                className="hidden"
                            />
                            <div
                                className={`w-6 h-6 border-2 rounded flex items-center justify-center ${
                                    acceptedTerms
                                        ? 'bg-green-500 border-green-500'
                                        : 'border-gray-300'
                                }`}
                            >
                                {acceptedTerms && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <span className="ml-3 text-sm font-medium text-gray-700">
                                I have read and agree to the Terms & Conditions
                            </span>
                        </label>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 border-t p-6 flex justify-between items-center">
                    <button
                        onClick={() => setShowTermsModal(false)}
                        className="flex items-center px-5 py-2 border rounded-lg text-gray-700 hover:bg-gray-100"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </button>

                    <button
                        onClick={proceedToPayment}
                        disabled={!acceptedTerms || termsLoading}
                        className="flex items-center px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {termsLoading ? (
                            <>
                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <CreditCard className="w-5 h-5 mr-2" />
                                Proceed to Payment
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TermsModal;
